import React from 'react';
import { Button, Group, Dialog, DialogProps, useToast } from '@economic/taco';
import { useNavigate } from 'react-router-dom';

import internalApiClient from '../../../../../shared/util/api/internalApiClient';
import track from '../../../../../shared/util/utilities/mixpanel';
import { t, tBB, useLocalization } from '../../../../../shared/components/l10n/l10n';

interface Props extends Partial<DialogProps> {
    isManualSyncAllowed: boolean;
    refreshSettings: () => void;
}

export const ManualSynchronisationDialog: React.FC<Props> = ({ isManualSyncAllowed, refreshSettings, ...dialogProps }) => {
    const navigate = useNavigate();
    const toast = useToast();
    const loadingTexts = useLocalization(textIds);

    if (loadingTexts) {
        return null;
    }

    const handleManualSync = (hide: () => void) => {
        track('smartbank', 'ongoing-use.bank-connections.manual-sync-button');

        internalApiClient
            .post('smartbank/manualSynchronization')
            .then(response => {
                if (response.data === null || response.data === '') {
                    toast.success(t`TOAST_SMART_BANK_MANUAL_SYNCHRONISATION_ACCOUNTS_SYNCED_SUCCESS`);
                } else if (response.data.indexOf('http') > -1) {
                    window.open(encodeURI(response.data), '_blank');
                }
                refreshSettings();
            })
            .catch(() => {
                toast.error(t`TOAST_SMART_BANK_MANUAL_SYNCHRONISATION_ERROR`);
            })
            .finally(() => hide());
    };

    const handleClose = () => {
        //when opened from bank reconciliation
        if (window.location.href.indexOf('#') > -1) {
            navigate(location.pathname);
        }
    };

    return (
        <Dialog size="xs" closeOnEscape={false} onClose={handleClose} {...dialogProps}>
            <Dialog.Content aria-label="sync-modal">
                {({ close }) => (
                    <>
                        <h2>{t`SMARTBANK_BANK_ACCOUNTS_PAGE_MANUAL_SYNCHRONIZATION_MODAL_TITLE`}</h2>
                        {tBB('SMARTBANK_BANK_ACCOUNTS_PAGE_MANUAL_SYNCHRONIZATION_MODAL_CONTENT')}
                        <Dialog.Footer>
                            <div className="mt-10 flex flex-row-reverse">
                                <Group>
                                    <Dialog.Close>
                                        <Button appearance="default">
                                            {t`SMARTBANK_BANK_ACCOUNTS_PAGE_MANUAL_SYNCHRONIZATION_DISMISS_BUTTON`}
                                        </Button>
                                    </Dialog.Close>
                                    <Button
                                        appearance="primary"
                                        type="submit"
                                        disabled={!isManualSyncAllowed}
                                        onClick={() =>
                                            handleManualSync(() => {
                                                close();
                                                handleClose();
                                            })
                                        }>
                                        {t`SMARTBANK_BANK_ACCOUNTS_PAGE_MANUAL_SYNCHRONIZATION_SYNC_BUTTON`}
                                    </Button>
                                </Group>
                            </div>
                        </Dialog.Footer>
                    </>
                )}
            </Dialog.Content>
            <Dialog.Extra>{t`SMARTBANK_BANK_ACCOUNTS_PAGE_MANUAL_SYNCHRONIZATION_MODAL_NOTICE`}</Dialog.Extra>
        </Dialog>
    );
};

const textIds = [
    'TOAST_SMART_BANK_MANUAL_SYNCHRONISATION_ACCOUNTS_SYNCED_SUCCESS',
    'TOAST_SMART_BANK_MANUAL_SYNCHRONISATION_ERROR',
    'SMARTBANK_BANK_ACCOUNTS_PAGE_MANUAL_SYNCHRONIZATION_MODAL_NOTICE',
    'SMARTBANK_BANK_ACCOUNTS_PAGE_MANUAL_SYNCHRONIZATION_MODAL_TITLE',
    'SMARTBANK_BANK_ACCOUNTS_PAGE_MANUAL_SYNCHRONIZATION_MODAL_CONTENT',
    'SMARTBANK_BANK_ACCOUNTS_PAGE_MANUAL_SYNCHRONIZATION_DISMISS_BUTTON',
    'SMARTBANK_BANK_ACCOUNTS_PAGE_MANUAL_SYNCHRONIZATION_SYNC_BUTTON',
];
